import '../orders/orders.scss';

import React, { useEffect, useState } from 'react';
import CloudIcon from '@mui/icons-material/Cloud';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RouteIcon from '@mui/icons-material/Route';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Card, Col, FormSelect, Row, FormCheck } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Container } from '@mui/material';
import { WHandDeliveryIcon } from '@wareclouds/wareclouds-ui';

import {
  getRoutePickupsPoints,
  getActiveRoute,
  optimizeRoute,
} from './services';

import { setMessage } from '../toasts/toastSlice';

import { CircularProgress, Grid } from '@mui/material';
import OptimizedRouteButtons from './OptimizedRouteButtons';
import { Trans, useTranslation } from 'react-i18next';
import { handleAxiosErrorMessage } from '../../utils';
import { NOTIFICATION_TYPES } from '../../config/consts/commons';
import { ROUTE_TYPES } from '../../config/consts/routes';
import env from '../../config/env';

const OptimizedRoute = () => {
  const { t } = useTranslation([
    'system',
    'title',
    'description',
    'glossary',
    'button',
  ]);
  const { username } = useSelector((state) => state.auth);
  const { routeType } = useSelector((state) => state.routes);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optimizationError, setOptimizationError] = useState(false);

  const [activeRoute, setActiveRoute] = useState({
    id_route: '',
    google_maps_route_link: '',
    waze_route_link: '',
  });

  const routeOptimizedGoogle =
    activeRoute.google_maps_route_link && activeRoute.id_route;
  const routeOptimizedWaze =
    activeRoute.waze_route_link && activeRoute.id_route;

  const [pickupPoints, setPickupPoints] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState('');
  const [noPickupSelectedError, setNoPickupSelectedError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showGoogleMapButtons, setShowGoogleMapButtons] = useState(true);
  const [showWazeMapButtons, setShowWazeButtons] = useState(false);

  const OPTIMIZE_ERROR_TTL = env.OPTIMIZE_ERROR_TTL
    ? parseInt(env.OPTIMIZE_ERROR_TTL)
    : 5000 * 60;

  useEffect(() => {
    checkOptimizeErrorExpiryTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const routesApi = async () => {
      setIsLoading(true);
      try {
        const { data } = await getActiveRoute({
          idClouder: username,
          routeType,
        });

        const { routes = [] } = data;
        setActiveRoute(routes[0] || {});

        const {
          id_route,
          google_maps_route_link,
          pending_orders,
          waze_route_link,
        } = routes[0] || {};

        if (
          activeRoute.re_optimize ||
          (!google_maps_route_link && !waze_route_link && !pending_orders)
        ) {
          const routePickupPoints = await getRoutePickupsPoints({ id_route });
          setPickupPoints(routePickupPoints.data);
        }
      } catch (err) {
        const errorMessage = handleAxiosErrorMessage(err);
        dispatch(
          setMessage({
            body: t(errorMessage, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
      setIsLoading(false);
    };

    if (username) {
      routesApi();
    }
  }, [activeRoute.re_optimize, dispatch, routeType, t, username]);

  const setOptimizeErrorExpiryTime = () => {
    const key = 'optimizeError';
    const now = new Date();
    const dataToStore = {
      value: 'waiting',
      expiry: now.getTime() + OPTIMIZE_ERROR_TTL,
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));

    setTimeout(() => {
      setOptimizationError(false);
      localStorage.removeItem(key);
    }, OPTIMIZE_ERROR_TTL);
  };

  const checkOptimizeErrorExpiryTime = () => {
    const key = 'optimizeError';
    const storedDataStr = localStorage.getItem(key);

    if (!storedDataStr) {
      setOptimizationError(false);
    } else {
      const storedData = JSON.parse(storedDataStr);
      const now = new Date();
      const expiryTimeRemaining = storedData.expiry - now.getTime();
      if (expiryTimeRemaining <= 0) {
        localStorage.removeItem(key);
        setOptimizationError(false);
      } else {
        setOptimizationError(true);
        setTimeout(() => {
          setOptimizationError(false);
          localStorage.removeItem(key);
        }, expiryTimeRemaining);
      }
    }
  };

  const handleSubmit = async () => {
    setNoPickupSelectedError('');
    setIsSubmitting(true);
    try {
      const payload = {
        id_route: activeRoute.id_route,
        warecloud_address: selectedPickup.address,
        warecloud_address_detail: selectedPickup.address_detail,
        warecloud_id_city: selectedPickup.id_city,
        force: activeRoute.re_optimize ? true : false,
      };

      const response = await optimizeRoute(payload);

      setActiveRoute(response.data);
      dispatch(
        setMessage({
          body: 'Se ha optimizado la ruta exitosamente',
          type: 'success',
        })
      );
    } catch (err) {
      const errorMessage = handleAxiosErrorMessage(err);
      if (errorMessage === 'ROUTE_OPTIMIZATION_SERVICE_ERROR') {
        setOptimizationError(true);
        dispatch(
          setMessage({
            body: t(errorMessage, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );

        setOptimizeErrorExpiryTime();
      } else {
        dispatch(
          setMessage({
            body: t(errorMessage, { ns: 'system' }),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const googleMapsRoutes = () => {
    try {
      return JSON.parse(activeRoute.google_maps_route_link) || [];
    } catch (error) {
      return [];
    }
  };

  const wazeRoutes = () => {
    try {
      return JSON.parse(activeRoute.waze_route_link) || [];
    } catch (error) {
      return [];
    }
  };

  if (isLoading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: '80vh' }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  if (!activeRoute.id_route) {
    return (
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Row className="mt-3">
          <h1 className="text-primary fw-bold">
            {t('routes.optimize', { ns: 'title' })}
          </h1>
        </Row>
        <Card className="route-card text-center">
          <Row className="w-100">
            <SentimentDissatisfiedIcon
              className="text-secondary w-100 fa-4x my-2"
              sx={{ fontSize: 60 }}
            />
          </Row>

          <p className="my-2">
            {t('routes.no_take_message_one', { ns: 'description' })}
          </p>
          <p className="my-2">
            <Trans t={t} i18nKey="routes.no_take_message_two" ns="description">
              Check if there are available routes in the routes tab or by
              clicking on{' '}
              <span
                className="text-primary fw-bold pointer"
                onClick={() => navigate('/routes/dashboard')}
              >
                here
              </span>
            </Trans>
            .
          </p>
        </Card>
      </Container>
    );
  }

  if (activeRoute.pending_orders) {
    return (
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Row className="mt-3">
          <h1 className="text-primary fw-bold">
            {t('routes.optimize', { ns: 'title' })}
          </h1>
        </Row>
        <Card className="route-card text-center">
          <Row className="w-100">
            <SentimentDissatisfiedIcon
              className="text-secondary w-100 fa-4x my-2"
              sx={{ fontSize: 60 }}
            />
          </Row>

          <p className="my-2">
            <Trans ns="description" t={t} i18nKey="routes.pending_message_one">
              To optimize your route, you should withdraw all orders from the
              corresponding wareclouds. That is, only have orders in the
              Deliveries "
              <WHandDeliveryIcon sx={{ fontSize: 20, marginX: '0.5rem' }} />
              Column".
            </Trans>
          </p>
          <p className="my-2">
            <Trans ns="description" t={t} i18nKey="routes.pending_message_two">
              Check which orders you have not yet marked as withdrawn{' '}
              <span
                className="text-primary fw-bold pointer"
                onClick={() => navigate('/orders/dashboard')}
              >
                here
              </span>
              .
            </Trans>
          </p>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3,
      }}
    >
      <Row className="mt-3">
        <h1 className="text-primary fw-bold">
          {t('routes.optimize', { ns: 'title' })}
        </h1>
      </Row>

      <Row className="my-2">
        <p className="text-primary fw-bold">
          <LocationOnIcon />{' '}
          {t('routes.choose_initial_point', { ns: 'description' })}
        </p>
      </Row>
      <Row>
        <Col xs="auto" className="p-0">
          <FormSelect
            name="id_pickup"
            onChange={(e) => {
              setSelectedPickup(pickupPoints[e.target.value]);
            }}
            className="my-2"
            disabled={
              !activeRoute.re_optimize &&
              (routeOptimizedGoogle || routeOptimizedWaze)
            }
          >
            <option value="">
              {t('select_option_optimized_default', { ns: 'description' })}
            </option>

            {pickupPoints.map((pickupPoint, index) => (
              <option key={`select-option-${index}`} value={index}>
                {pickupPoint.first_name}: {pickupPoint.address}
                {pickupPoint.address_detail
                  ? `, ${pickupPoint.address_detail}`
                  : ''}
                , {pickupPoint.city_name}
              </option>
            ))}
          </FormSelect>
        </Col>
      </Row>
      <Row className="justify-content-center my-3">
        <Col className="text-center">
          <Button
            sx={{
              textTransform: 'none',
            }}
            disabled={
              isSubmitting ||
              (!activeRoute.re_optimize &&
                (routeOptimizedGoogle ||
                  routeOptimizedWaze ||
                  optimizationError)) ||
              routeType === ROUTE_TYPES.EXPRESS
            }
            variant="contained"
            onClick={() => {
              selectedPickup
                ? handleSubmit()
                : setNoPickupSelectedError(
                    t('MUST_SELECT_PICK_UP_POINT', { ns: 'system' })
                  );
            }}
          >
            {isSubmitting ? (
              'Procesando...'
            ) : (
              <Row>
                <Col>
                  <RouteIcon className="sort-icon" />
                </Col>
                <Col xs="auto">
                  <p>
                    {activeRoute.re_optimize
                      ? t('re_optimize', { ns: 'button' })
                      : t('optimize', { ns: 'button' })}
                  </p>
                </Col>
              </Row>
            )}
          </Button>
          {optimizationError && (
            <p className="text-danger">
              <small>
                {t('ROUTE_OPTIMIZATION_SERVICE_ERROR', { ns: 'system' })}
              </small>
            </p>
          )}
          {noPickupSelectedError && (
            <p className="text-danger">
              <small>{noPickupSelectedError}</small>
            </p>
          )}
        </Col>
      </Row>
      {!(routeOptimizedGoogle || routeOptimizedWaze) && (
        <Row className="my-2">
          <Col className="text-center">
            <p className="text-success">
              {t('routes.optimized', { ns: 'title' })} 🎉
            </p>
            <p>
              <Trans ns="description" i18nKey="routes.optimized" t={t}>
                Your orders to be delivered{' '}
                <strong>
                  are now sorted according to your optimized route
                </strong>
              </Trans>
              😉.
            </p>
          </Col>
          <FormCheck
            checked={showGoogleMapButtons}
            className="mt-2"
            id="google-maps-switch"
            label={t('see_google_maps', { ns: 'description' })}
            onChange={(_) => setShowGoogleMapButtons(!showGoogleMapButtons)}
            type="switch"
          />
          <FormCheck
            checked={showWazeMapButtons}
            className="mb-2"
            id="waze-switch"
            label={t('see_waze', { ns: 'description' })}
            onChange={(_) => setShowWazeButtons(!showWazeMapButtons)}
            type="switch"
          />
        </Row>
      )}

      {routeOptimizedGoogle && showGoogleMapButtons && (
        <OptimizedRouteButtons
          icon={LocationOnIcon}
          multipleButtonsLabel={'Parte'}
          routes={googleMapsRoutes()}
          serviceLabel={'Google Maps'}
        />
      )}
      {routeOptimizedWaze && showWazeMapButtons && (
        <OptimizedRouteButtons
          icon={CloudIcon}
          multipleButtonsLabel={t('orders', { ns: 'glossary' })}
          routes={wazeRoutes()}
          serviceLabel={'Waze'}
        />
      )}
    </Container>
  );
};

export default OptimizedRoute;
